import React, { useState, useContext, useEffect, useRef, useCallback } from 'react'
import { dismissModal } from '../../../service/modal-service';
import { SystemError } from '../../error/SystemError'
import { motion } from 'framer-motion'
import Image from 'next/image'
import { uploadPost } from '../../../api/post/buzz-api';
import { UserContext } from '../../../context/UserContext';
import BottomSheet from '../../commons/BottomSheet';
import { Video, ImageIcon, Globe, Users, Loader2, Send, PenLine, Pencil } from 'lucide-react';
import ReactPlayer from 'react-player';
import ImageEditor from '../../commons/media-editor/ImageEditor';


export const NewBuzz = (props) => {
    let refreshBuzz = props.refreshBuzz

    const inputRef = useRef()

    const [isOpen, setIsOpen] = useState(true)
    const [postStatement, setPostStatement] = useState('')
    const [postSecurity, setPostSecurity] = useState('public')

    //File Attachment
    const [filesToAttach, setFilesToAttach] = useState({})
    const filesToUpload = useRef({})
    const [uploadPercent, setUploadPercent] = useState(0)

    const [isSubmit, setIsSubmit] = useState(false)
    const [isSystemError, setIsSystemError] = useState(false)
    const [errorContent, setErrorContent] = useState({'header': "", 'body': ""})

    // Image Editor State
    const [isImageEditorOpen, setIsImageEditorOpen] = useState(false)
    const [originalImageSrc, setOriginalImageSrc] = useState(null)

    const { userId, userFullName, userProfilePic } = useContext(UserContext)

    const handleClose = () => {
        dismissModal()
    }

    const changePostSecurity = (e) => {
        setPostSecurity(e.target.value)
    }

    const changePostStatement = (e) =>{
        setPostStatement(e.target.value)
    }

    const removeFile = () => {
        setFilesToAttach("")
        filesToUpload.current = {}
        setOriginalImageSrc(null)
    }

    // Open Image Editor
    const openImageEditor = () => {
        if (filesToAttach?.media && filesToAttach?.fileType?.includes("image")) {
            // Store original image if not already stored (for re-editing from original)
            if (!originalImageSrc) {
                setOriginalImageSrc(filesToAttach.media)
            }
            setIsImageEditorOpen(true)
        }
    }

    // Handle edited image from ImageEditor
    const handleImageEdited = ({ dataURL, file }) => {
        // Update the display preview
        setFilesToAttach({
            media: dataURL,
            fileType: file.type
        })
        // Update the file to upload
        filesToUpload.current = {
            media: file,
            fileType: file.type
        }
        setIsImageEditorOpen(false)
    }

    const addFileToDisplay = async(media, file) => {
        if (media != ""){
            let mediaW = {
                'media': "",
                'fileType': ""
            }
            mediaW.media = media;
            mediaW.fileType = file.type;
            setFilesToAttach(mediaW);
        }
    }
    
    const addFileToUpload = (file) => {
        if (file != ""){
            var mediaW = {
                'media': "",
                'fileType':""
            }
            mediaW.media = file;
            mediaW.fileType = file.type;
            filesToUpload.current = mediaW;
        } 
    }

    //When an image is selected
    const handleFileAttachment =(e) => {
        if(e.target.files.length == 0) return;
        var files = e.target.files;
        if(files.length === 0){
            return
        } else {
            // Reset original image when a new file is selected
            setOriginalImageSrc(null)
            for(let file of files){  
                if(file.size > 10485760){
                    setErrorContent({'header': "File size exceeded", 'body': "Please try again with a file size less than 10MB."});
                    setIsSystemError(true)
                    return;
                }
                addFileToUpload(file);
                var reader = new FileReader();
                reader.onload = async(e) => {
                    await addFileToDisplay(e.target.result, file);
                };
                reader.readAsDataURL(file);	
            }  
        };
    } 

    //When a video is selected
    const handleVideoFileAttachment =(e) => {
        if(e.target.files.length == 0) return;
        var files = e.target.files;
        if(files.length === 0){
            return
        } else {
            for(let file of files){  
                if(file.size > 104857600){
                    setErrorContent({'header': "File size exceeded", 'body': "Please try again with a file size less than 100MB."});
                    setIsSystemError(true)
                    return;
                }
                addFileToUpload(file);
                var reader = new FileReader();
                reader.onload = async(e) => {
                    await addFileToDisplay(e.target.result, file);
                };
                reader.readAsDataURL(file);	
              }  
        };
    }

    const submitPost = () => {
        setIsSubmit(true)
    }

    const uploadBuzz = useCallback(async() => {
        try {
            let response = await uploadPost(userId, userFullName, filesToUpload.current?.media, filesToUpload.current?.fileType, "Uploads", postStatement, postSecurity)
            if( response != null ){
                refreshBuzz()
                setIsSubmit(false)
                dismissModal()
            }
        } catch (error) {
            console.log(error)
            setErrorContent({'header': "Error Posting", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
            setIsSubmit(false)
            return;
        }
    }, [userId, userFullName, postStatement, postSecurity, refreshBuzz])

    useEffect(() => {
        if(!isSubmit) return
        uploadBuzz()
    }, [isSubmit, uploadBuzz])

    // Debounced auto resize textarea for better performance
    const handleTextareaChange = (e) => {
        setPostStatement(e.target.value);
        // Auto resize inline for immediate feedback
        const textarea = e.target;
        textarea.style.height = 'auto';
        textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    };

    const canPost = postStatement.trim() || Object.keys(filesToAttach).length > 0;

    return (
        <>
            <BottomSheet isOpen={isOpen} onClose={handleClose} maxWidth="lg" maxHeight="100vh">
                <BottomSheet.Header>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                            <PenLine className="w-5 h-5 text-orange-600" />
                        </div>
                        <BottomSheet.Title>Create Post</BottomSheet.Title>
                    </div>
                </BottomSheet.Header>

                <BottomSheet.Content>
                    <div className="space-y-4">
                        {/* User Info & Security */}
                        <div className="flex items-center gap-3">
                            <div className="w-11 h-11 rounded-full bg-linear-to-br from-orange-400 to-amber-400 flex items-center justify-center text-white font-bold">
                                {userFullName?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div className="flex-1">
                                <p className="font-semibold text-gray-900 text-sm">{userFullName}</p>
                                {/* Post Security Toggle */}
                                <div className="flex items-center gap-3 mt-1">
                                    <label className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs cursor-pointer transition-all ${postSecurity === 'public' ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-500'}`}>
                                        <input
                                            type="radio"
                                            name="postSecurity"
                                            value="public"
                                            checked={postSecurity === 'public'}
                                            onChange={changePostSecurity}
                                            className="hidden"
                                        />
                                        <Globe className="w-3 h-3" />
                                        <span>Public</span>
                                    </label>
                                    <label className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs cursor-pointer transition-all ${postSecurity === 'strings' ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-500'}`}>
                                        <input
                                            type="radio"
                                            name="postSecurity"
                                            value="strings"
                                            checked={postSecurity === 'strings'}
                                            onChange={changePostSecurity}
                                            className="hidden"
                                        />
                                        <Users className="w-3 h-3" />
                                        <span>Connections</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Textarea Input */}
                        <textarea
                            name="description"
                            ref={inputRef}
                            className="w-full resize-none text-gray-800 placeholder-gray-400 text-base leading-relaxed focus:outline-none min-h-[100px] bg-gray-50 rounded-xl p-3"
                            placeholder="What's on your mind?"
                            value={postStatement}
                            onChange={handleTextareaChange}
                            autoFocus
                        />

                        {/* Media Preview */}
                        {Object.keys(filesToAttach).length > 0 && (
                            <motion.div 
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="relative rounded-2xl overflow-hidden bg-gray-100"
                            >
                                {filesToAttach.fileType.includes("image") && (
                                    // Use plain img to show full image at its natural aspect ratio
                                    <img
                                        src={filesToAttach.media}
                                        alt="Selected"
                                        className="w-full rounded-2xl"
                                        style={{ display: 'block' }}
                                    />
                                )}
                                {filesToAttach.fileType.includes("gif") && (
                                    <img
                                        src={filesToAttach.media}
                                        alt="Selected GIF"
                                        className="w-full rounded-2xl"
                                        style={{ display: 'block' }}
                                    />
                                )}
                                {filesToAttach.fileType.includes("video") && (
                                    <ReactPlayer
                                        src={filesToAttach.media}
                                        playing
                                        muted
                                        loop
                                        controls={false}
                                        width="100%"
                                        height="auto"
                                        style={{ borderRadius: '16px' }}
                                    />
                                )}
                                {/* Action Buttons (top-right corner) */}
                                <div className="absolute top-3 right-3 flex items-center gap-2">
                                    {/* Edit Photo Button - only for images, not gifs or videos */}
                                    {filesToAttach.fileType?.includes("image") && !filesToAttach.fileType?.includes("gif") && (
                                        <motion.button
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                            type="button"
                                            title="Edit photo"
                                            className="w-10 h-10 flex items-center justify-center text-white bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full transition-colors"
                                            onClick={openImageEditor}
                                        >
                                            <Pencil className="w-5 h-5" />
                                        </motion.button>
                                    )}
                                    {/* Remove File Button */}
                                    <motion.button
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        type="button"
                                        title="Remove file"
                                        className="w-10 h-10 flex items-center justify-center text-white bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full transition-colors"
                                        onClick={removeFile}
                                    >
                                        <span>X</span>
                                    </motion.button>
                                </div>
                            </motion.div>
                        )}

                        {/* Media Attachment Buttons */}
                        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
                            <span className="text-xs text-gray-500 mr-2">Add to post:</span>
                            <label className="p-2.5 hover:bg-orange-100 rounded-full cursor-pointer transition-colors group">
                                <ImageIcon className="w-5 h-5 text-gray-400 group-hover:text-orange-500 transition-colors" />
                                <input
                                    type="file"
                                    accept="image/*"
                                    capture={false}
                                    onChange={handleFileAttachment}
                                    multiple
                                    className="hidden"
                                />
                            </label>
                            <label className="p-2.5 hover:bg-orange-100 rounded-full cursor-pointer transition-colors group">
                                <Video className="w-5 h-5 text-gray-400 group-hover:text-orange-500 transition-colors" />
                                <input
                                    type="file"
                                    accept="video/*"
                                    capture={false}
                                    onChange={handleVideoFileAttachment}
                                    className="hidden"
                                />
                            </label>
                        </div>
                    </div>
                </BottomSheet.Content>

                <BottomSheet.Footer>
                    <div className="flex gap-3">
                        <BottomSheet.CloseButton>Cancel</BottomSheet.CloseButton>
                        <motion.button
                            whileTap={{ scale: 0.95 }}
                            onClick={submitPost}
                            disabled={isSubmit || !canPost}
                            className="flex-1 px-4 py-3 rounded-xl font-semibold text-white bg-linear-to-r from-orange-500 to-amber-500 shadow-lg shadow-orange-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {isSubmit ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <>
                                    <Send className="w-4 h-4" />
                                    Post
                                </>
                            )}
                        </motion.button>
                    </div>
                </BottomSheet.Footer>
            </BottomSheet>

            {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError} />}

            {/* Image Editor Modal */}
            <ImageEditor
                isOpen={isImageEditorOpen}
                onClose={() => setIsImageEditorOpen(false)}
                onSave={handleImageEdited}
                imageSrc={originalImageSrc || filesToAttach?.media}
            />
        </>
    )
}
